import React, { forwardRef } from 'react';
import { GalleryTile, GalleryTileWithRef } from '../../../../GalleryTile';
import { templateToTitle } from '@apps/card/utils/templateToTitle';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { useAuth } from '@shared/components/AuthProvider';
import { tileRowGap } from '../../CardsConfig';
import { CardThumbnail } from './CardThumbnail';
import { useTileAltText } from './useTileAltText';
import type { CardTileProps } from './CardTile.types';

type CardTileStaticProps = CardTileProps &
  Readonly<{
    aspectRatio: string;
    /** Above-the-fold tiles load their image eagerly. */
    imageLoading: 'eager' | 'lazy';
    /** The shell tracks focus so it can hand it to the live tile that replaces this one. */
    onFocus?: () => void;
    onBlur?: () => void;
  }>;

/**
 * The public gallery's tile before it is near the viewport (PRNT-5494): the link, the print
 * thumbnail and the name, and nothing that costs — no live preview, swatches, favorite
 * mutations or observers. This is what the server renders for every tile, so a crawler sees the
 * whole catalog; `CardTileShell` swaps in the live `CardTile` once the tile nears the viewport.
 * A plain link: only a crawler, or a visitor before hydration, can click it, and a full
 * navigation is right for both. Like the live tile, an unapproved ("coming soon") design gets no
 * link unless the viewer is a super admin. No `aria-label`: with no controls inside, the thumbnail's
 * alt text and the title are the link's name.
 */
export const CardTileStatic = forwardRef<HTMLDivElement, CardTileStaticProps>(function CardTileStatic(
  { data, aspectRatio, imageLoading, preferredFormat, currentCategory, onFocus, onBlur },
  ref,
) {
  const { getProductDetailsPagePath } = useCardsRouterContext();
  const { isSuperAdmin } = useAuth();
  const altText = useTileAltText(data, currentCategory);
  const title = templateToTitle(data);
  const canViewPdp = !!(data.approved || isSuperAdmin);

  return (
    <GalleryTileWithRef
      ref={ref}
      aspectRatio={aspectRatio}
      alignContent="start"
      rowGap={tileRowGap}
      {...(canViewPdp
        ? {
            as: 'a' as const,
            href: getProductDetailsPagePath('wedding', data.category, data.themeId, data.id, preferredFormat),
            role: undefined,
            'data-group': true,
            color: 'inherit',
            textDecoration: 'none',
            onFocus,
            onBlur,
          }
        : {})}
    >
      <CardThumbnail
        template={{ id: data.id, themeId: data.themeId }}
        fallbackImageUrl={data.thumbnailUrl ?? data.digitalDesignThumbnailUrl}
        fallbackCardSize={{ widthInches: data.widthInches, heightInches: data.heightInches }}
        fallbackFitsLivePreview
        imageLoading={imageLoading}
        altText={altText}
      />
      <GalleryTile.Content title={title} />
    </GalleryTileWithRef>
  );
});
